import React from 'react';
import {useFormContext} from "react-hook-form";
import {classNames} from "@utils/class-names";

interface PhoneFormFieldProps {
    name: string;
    label: string;
    isRequired?: boolean;
    className?: string;
}

const PhoneFormField: React.FC<PhoneFormFieldProps> = ({
    name,
    label,
    isRequired = false,
    className = ''
}) => {
    const {register, formState: {errors, isSubmitting, disabled}, setValue} = useFormContext();

    const formatPhoneNumber = (value: string): string => {
        // Usuń wszystkie znaki oprócz cyfr
        const numbers = value.replace(/\D/g, '');
        
        // Jeśli mamy 3 lub więcej cyfr, dodaj pierwszy myślnik
        if (numbers.length >= 3) {
            const firstPart = numbers.slice(0, 3);
            const remaining = numbers.slice(3);
            
            if (remaining.length >= 3) {
                // Jeśli mamy 6 lub więcej cyfr, dodaj drugi myślnik
                const secondPart = remaining.slice(0, 3);
                const lastPart = remaining.slice(3);
                return `${firstPart}-${secondPart}-${lastPart}`;
            } else {
                return `${firstPart}-${remaining}`;
            }
        }
        
        return numbers;
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        const formatted = formatPhoneNumber(value);
        
        // Ogranicz do 11 znaków (xxx-xxx-xxx)
        if (formatted.length <= 11) {
            setValue(name, formatted);
        }
    };

    // Konwertuj z formatu bazy danych (xxxxxxxxx) na format wyświetlania (xxx-xxx-xxx)
    const formatForDisplay = (value: string): string => {
        if (!value) return '';
        // Jeśli już ma format xxx-xxx-xxx, zwróć jak jest
        if (value.includes('-')) return value;
        // Jeśli to 9 cyfr, sformatuj do xxx-xxx-xxx
        if (value.length === 9 && /^\d{9}$/.test(value)) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
        }
        return value;
    };

    // Konwertuj z formatu wyświetlania (xxx-xxx-xxx) na format bazy danych (xxxxxxxxx)
    const formatForDatabase = (value: string): string => {
        if (!value) return '';
        // Usuń wszystkie myślniki, zostaw tylko cyfry
        return value.replace(/\D/g, '');
    };

    return (
        <div className={`w-full ${className}`}>
            <label htmlFor={name} className={classNames(
                "text-[14px] font-semibold block mb-2 leading-[1.125rem] text-on-surface/80"
            )}>
                {label}
                {isRequired && <span className='text-red-500'> *</span>}
            </label>
            
            <input
                type="tel"
                id={name}
                {...register(name)}
                placeholder="xxx-xxx-xxx"
                maxLength={11}
                className={classNames(
                    `text-[14px] max-h-[40px] border border-outline bg-surface-level-1 rounded-lg px-3 py-[11px] appearance-none w-full focus:outline-none focus:ring-2 focus:ring-primary`,
                    errors[name] ? 'border border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-primary_400',
                    className
                )}
                disabled={isSubmitting || disabled}
                onInput={handleInput}
            />
            
            {errors[name] && (
                <p className="text-red-500 text-xs px-3 pt-1">
                    {String(errors[name]?.message)}
                </p>
            )}
        </div>
    );
};

export default PhoneFormField;
